import { IOrderInfo, ISymbol, ITicker, Market } from './market-base.class';
import { IMarketSettingsToken }                 from './IMarketSettingsToken';
import { parseToFirstNonZero }                  from '../utils';
import HmacSHA512                               from 'crypto-js/hmac-sha512';

type BithumbResponse = {
    status:     string;
    data?:      any;
    message?:   string;
    order_id?:  string;
}

export class MarketBithumbKor extends Market<IMarketSettingsToken>
{
    protected name:              string      = 'BitHumb';
    protected icon:              string      = '/assets/images/exchanges/bithumb.svg';

    private accessUrl:           string      = 'https://api.bithumb.com';
    private currencies:          string[]    = [];
    private symbols:             ISymbol[]   = [];
    private prefferedCurrencies: string[]    = ['KRW', 'JASMY', 'ETH'];
    private balances:            Object      = {};
    private forceBalances:       boolean     = false;
    private proxyApi:            string      = '/api/market/bithumb-kor';

    private readonly STATUS_OK:  string      = "0000";

    public async checkConnection(): Promise<boolean>
    {
        await this.getBalance('ETH');

        return true;
    }

    public async getCurrencies(): Promise<string[]>
    {
        if (this.currencies.length > 0) return this.currencies;

        const endpoint = '/public/ticker/ALL_KRW';
        const url      = `${this.accessUrl}${endpoint}`;

        const response = <BithumbResponse>await this.http.get(url).toPromise();

        if (response.status === this.STATUS_OK)
        {
            const coins     = ['KRW', ...Object.keys(response.data)];
            this.currencies = this.sortCurrencies(coins);

            return this.currencies;
        }
        else
        {
            throw new Error(response.message);
        }
    }

    public async getSymbols(): Promise<ISymbol[]>
    {
        await this.getCurrencies();

        this.symbols = this.currencies.map((_currency): ISymbol => {
                                          return {
                                              symbol: `${_currency}_KRW`,
                                              quote_currency: 'KRW',
                                              base_currency: _currency,
                                              precision: '4'
                                          }
                                      });

        return this.symbols;
    }

    public async getBalance(_currency: string): Promise<string>
    {
        if (!!this.balances[_currency] && !this.forceBalances) return this.balances[_currency];

        const endpoint = '/info/balance';
        const url      = `${this.accessUrl}${endpoint}`;
        const coin     = _currency.toLowerCase() === 'krw' ? 'btc' : _currency;

        const params = {
            endPoint: endpoint,
            currency: coin.toLowerCase()
        };

        const queryString = this.createQueryString(params);
        const headers   = this.getHeaders(endpoint, queryString);

        const response = <BithumbResponse>await this.http.post(this.proxyApi, {
                                                            url,
                                                            method: 'POST',
                                                            body: params,
                                                            headers
                                                         })
                                                         .toPromise();

        if (response.status === this.STATUS_OK)
        {
            this.balances[_currency] = response.data[`available_${_currency.toLowerCase()}`];
            this.balances['krw']     = response.data[`available_krw`];

            return this.balances[_currency];
        }
        else
        {
            throw new Error(response['message']);
        }
    }

    public async getTicker(_symbol: string): Promise<ITicker>
    {
        const endpoint = `/public/ticker/${_symbol.toUpperCase()}`;

        const url = `${this.accessUrl}${endpoint}`;

        const response = <BithumbResponse>await this.http.get(url).toPromise();

        if (response.status === this.STATUS_OK)
        {
            const minPrice = Number(response.data.min_price);
            const maxPrice = Number(response.data.max_price);

            const avgPrice = parseToFirstNonZero((minPrice + maxPrice) / 2, 2, 4);

            return {
                buy:  avgPrice,
                sell: avgPrice
            };
        }
        else
        {
            throw new Error(response['message']);
        }
    }

    public getOrderInfoUrl(_orderId: string, _symbol?: string): {url: string, data: any}
    {
        const currency = this.coinsService.parseExchangeSymbol(_symbol)[0];
        const endpoint = '/info/orders';

        const params = {
            endPoint:       endpoint,
            order_id:       _orderId,
            order_currency: currency
        };

        const queryString = this.createQueryString(params);
        const headers   = this.getHeaders(endpoint, queryString);

        return {
            url: `${this.accessUrl}${endpoint}`,
            data: {
                body: params,
                headers
            }
        }
    }

    public async getOrderInfo(_orderId: string, _symbol: string): Promise<IOrderInfo>
    {
        const currency = this.coinsService.parseExchangeSymbol(_symbol)[0];
        const endpoint = '/info/order_detail';

        const params = {
            endPoint:       endpoint,
            order_id:       _orderId,
            order_currency: currency
        };

        const queryString = this.createQueryString(params);
        const headers   = this.getHeaders(endpoint, queryString);

        const url = `${this.accessUrl}${endpoint}`;

        const response = <BithumbResponse>await this.http.post(this.proxyApi, {
                                                             url,
                                                             method: 'POST',
                                                             body: params,
                                                             headers
                                                         })
                                                         .toPromise();

        if (response.status === this.STATUS_OK)
        {
            const orderData = response.data;

            if (!orderData)
            {
                throw new Error('ORDER_NOT_FOUND');
            }

            return this.parseOrderInfo(orderData);
        }
        else
        {
            throw new Error(response['message']);
        }
    }

    public async getOrdersHistory(_symbol): Promise<IOrderInfo[]>
    {
        const currencies = this.coinsService.parseExchangeSymbol(_symbol);
        const endpoint = '/info/user_transactions';

        const params = {
            endPoint:         endpoint,
            order_currency:   currencies[0],
            payment_currency: currencies[1]
        };

        const queryString = this.createQueryString(params);
        const headers   = this.getHeaders(endpoint, queryString);

        const url = `${this.accessUrl}${endpoint}`;

        const response = <BithumbResponse>await this.http.post(this.proxyApi, {
                                                             url,
                                                             method: 'POST',
                                                             body: params,
                                                             headers
                                                         })
                                                         .toPromise();

        if (response.status === this.STATUS_OK)
        {
            return response.data.filter((_orderData) => ['1', '2'].includes(_orderData.search))
                                .map((_orderData) => this.parseTransactionInfo(_orderData));
        }
        else
        {
            throw new Error(response['message']);
        }
    }

    public async createOrder(
        _symbol: string,
        _amount: string,
        _type: 'buy' | 'sell'
    ): Promise<string>
    {

        const endpoint      = `/trade/market_${_type}`;
        const currencies    = this.coinsService.parseExchangeSymbol(_symbol);
        const ticker        = await this.getTicker(_symbol);
        const url           = `${this.accessUrl}${endpoint}`;

        let amount = _amount.toString();

        if (_type === 'buy')
        {
            const decimals = ticker.buy.split('.')[1]?.length || 0;
            amount = (Number(_amount) / Number(ticker.buy)).toFixed(decimals);
        }

        const params = {
            endPoint:         endpoint,
            units:            amount.toString(),
            order_currency:   currencies[0],
            payment_currency: currencies[1]
        };

        const queryString = this.createQueryString(params);
        const headers   = this.getHeaders(endpoint, queryString);

        const response = <BithumbResponse>await this.http.post(this.proxyApi, {
                                                             url,
                                                             method: 'POST',
                                                             body: params,
                                                             headers
                                                         })
                                                         .toPromise();

        if (response.status === this.STATUS_OK)
        {
            this.balances = {};
            return response.order_id;
        } else
        {
            throw new Error(this.parseOrderResponse(response));
        }
    }

    private parseOrderInfo(_orderInfo: any): IOrderInfo
    {
        const fiatAmount  = _orderInfo.contract.reduce((prev, _contract) => Number(_contract.total) + prev, 0).toString();
        const coinsAmount = _orderInfo.contract.reduce((prev, _contract) => Number(_contract.units) + prev, 0).toString();
        const price       = (_orderInfo.contract.reduce((prev, _contract) => Number(_contract.price) + prev, 0) / _orderInfo.contract.length).toString();

        const sendAmount = _orderInfo.type === 'ask' ? coinsAmount : fiatAmount;
        const getAmount  = _orderInfo.type === 'ask' ? fiatAmount : coinsAmount;

        return {
            orderId:        _orderInfo.order_id,
            sendAmount:     sendAmount.toString(),
            getAmount:      getAmount.toString(),
            fee:            null,
            finishedAt:     (_orderInfo.order_date || _orderInfo.transfer_date).toString().slice(0, -3),
            price:          price,
            state:          _orderInfo.order_status === 'Completed' ? 'complete' : 'processing',
            type:           _orderInfo.type === 'ask' ? 'sell' : 'buy',
            symbol:         `${_orderInfo.order_currency}_${_orderInfo.payment_currency}`,
            currencySend:   _orderInfo.type === 'ask' ? _orderInfo.order_currency : _orderInfo.payment_currency,
            currencyGet:    _orderInfo.type === 'ask' ? _orderInfo.payment_currency : _orderInfo.order_currency
        };
    }

    private parseTransactionInfo(_orderInfo: any): IOrderInfo
    {
        const sendAmount = _orderInfo.search === '1' ? _orderInfo.amount : _orderInfo.units;//_orderInfo['type'] === 'bid' ? _orderInfo['tradeTotal'] : _orderInfo['tradedNum'];
        const getAmount  = _orderInfo.search === '1' ? _orderInfo.units : _orderInfo.amount; //_orderInfo['side'] === 'buy' ? _orderInfo['tradedNum'] : _orderInfo['tradeTotal'];

        return {
            orderId:        _orderInfo.order_id,
            sendAmount:     sendAmount.toString(),
            getAmount:      getAmount.toString(),
            fee:            null,
            finishedAt:     (_orderInfo.order_date || _orderInfo.transfer_date).toString().slice(0, -3),
            price:          _orderInfo.price,
            state:          'complete',
            type:           _orderInfo.search === '1' ? 'buy' : 'sell',
            symbol:         `${_orderInfo.order_currency}_${_orderInfo.payment_currency}`,
            currencySend:   _orderInfo.search === '1' ? _orderInfo.order_currency : _orderInfo.payment_currency,
            currencyGet:    _orderInfo.search === '1' ? _orderInfo.payment_currency : _orderInfo.order_currency
        };
    }

    private parseOrderResponse(_response: any)
    {
        if (Boolean(_response['message']))
        {
            return _response['message'];
        }

        return 'FORM.ERRORS.SMTH-WENT-WRONG';
    }

    private createQueryString(_params: Object = {})
    {
        const result = [];

        for (let key in _params)
        {
            const encodedKey   = encodeURIComponent(key.replace(/[!'()*]/g, escape));
            const encodedValue = encodeURIComponent(_params[key].replace(/[!'()*]/g, escape));

            result.push(`${encodedKey}=${encodedValue}`)
        }

        return result.join('&');
    }

    private getPreSignedText(_endpoint: string, _queryParams: string, _nonce: number)
    {
        return _endpoint + this.chr(0) + _queryParams + this.chr(0) + _nonce;
    }

    private getQuerySignature(_query: string)
    {
        const hash = HmacSHA512(_query, this.settings.secret_key).toString();

        const base64Hash = btoa(hash);

        return base64Hash;
    }

    private getHeaders(_endpoint: string, _query: string): Object
    {
        const nNonce = Date.now();
        const preSignedText = this.getPreSignedText(_endpoint, _query, nNonce);

        return {
            'Api-Key' : this.settings.access_key,
            'Api-Sign' : this.getQuerySignature(preSignedText),
            'Api-Nonce' : nNonce
        };
    }

    private sortCurrencies(_currencies: string[]): string[]
    {
        return _currencies.sort((a, b) =>
        {
            const indexA = this.prefferedCurrencies.indexOf(a);
            const indexB = this.prefferedCurrencies.indexOf(b);
            if (indexA === -1) return 0;

            if (indexA < indexB) return -1;

            if (indexA !== -1 && indexB === -1) return -1;

            if (indexA > indexB) return 1;

            return 0;
        });
    }

    /**
     * Get from Bithumb.com API example
     */
    private chr(codePt) {
        //  discuss at: http://phpjs.org/functions/chr/
        // original by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
        // improved by: Brett Zamir (http://brett-zamir.me)
        //   example 1: chr(75) === 'K';
        //   example 1: chr(65536) === '\uD800\uDC00';
        //   returns 1: true
        //   returns 1: true

        if (codePt > 0xFFFF) { // Create a four-byte string (length 2) since this code point is high
            //   enough for the UTF-16 encoding (JavaScript internal use), to
            //   require representation with two surrogates (reserved non-characters
            //   used for building other characters; the first is "high" and the next "low")
            codePt -= 0x10000;
            return String.fromCharCode(0xD800 + (codePt >> 10), 0xDC00 + (codePt & 0x3FF));
        }

        return String.fromCharCode(codePt);
    }
}

